import { EnergySeries, EvchargeSeries, PowerSeries } from '.';
import { coreServicesLabels } from '../../core-services.service';

export const getStyleProp = (prop: string) =>
  window.getComputedStyle(document.querySelector('body') as any).getPropertyValue(prop);

export const applyColors = <T extends EnergySeries | PowerSeries | EvchargeSeries | { [key: string]: any; }>(data?: T): T => {
  if (!data) {
    throw new Error('Missing telemetry');
  }

  // ENERGIES
  data.EG1 = {
    ...data.EG1 || {},
    color: getStyleProp('--orange-500'),
  };
  data.EG2 = {
    ...data.EG2 || {},
    color: getStyleProp('--grey-300'),
  };
  data.EG3 = {
    ...data.EG3 || {},
    color: getStyleProp('--grey-400'),
  };
  data.EG4 = {
    ...data.EG4 || {},
    color: getStyleProp('--blue-500'),
  };
  data.EG5 = {
    ...data.EG5 || {},
    color: getStyleProp('--green-400'),
  };
  data.EG6 = {
    ...data.EG6 || {},
    color: getStyleProp('--green-500'),
  };

  // POWERS
  data.P_DC_TOT = {
    ...data.P_DC_TOT || {},
    color: getStyleProp('--orange-500'),
  };
  data.P_EXP = {
    ...data.P_EXP || {},
    color: getStyleProp('--grey-300'),
  };
  data.P3 = {
    ...data.P3 || {},
    color: getStyleProp('--grey-400'),
  };
  data.P_LOAD = {
    ...data.P_LOAD || {},
    color: getStyleProp('--blue-500'),
  };
  data.P_B_AGGR = {
    ...data.P_B_AGGR || {},
    color: getStyleProp('--green-400'),
  };
  data.P_B_AGGR = {
    ...data.P_B_AGGR || {},
    color: getStyleProp('--green-500'),
  };
  data.BP = {
    ...data.BP || {},
    color: getStyleProp('--green-400'),
  };
  data.BP1 = {
    ...data.BP1 || {},
    color: getStyleProp('--green-400'),
  };
  data.BP2 = {
    ...data.BP2 || {},
    color: getStyleProp('--green-500'),
  };
  // solarimeter telemetry
  data.SOL_IRR = {
    ...data.SOL_IRR || {},
    color: getStyleProp('--yellow-500'),
  };
  data.SOL_TEMP = {
    ...data.SOL_TEMP || {},
    color: getStyleProp('--red-400'),
  };
  data.SOL_EG = {
    ...data.SOL_EG || {},
    color: getStyleProp('--yellow-500'),
  };
  // evcharge telemetry
  data.fast = {
    ...data.fast || {},
    color: getStyleProp('--red-500'),
  };
  data.green = {
    ...data.green || {},
    color: getStyleProp('--green-500'),
  };
  data.timePower = {
    ...data.timePower || {},
    color: getStyleProp('--orange-500'),
  };
  data.kmRepeat = {
    ...data.kmRepeat || {},
    color: getStyleProp('--yellow-500'),
  };
  data.kmSingle = {
    ...data.kmSingle || {},
    color: getStyleProp('--yellow-500'),
  };
  data.greenMixed = {
    ...data.greenMixed || {},
    color: getStyleProp('--green-500'),
  };
  data.timePowerSingle = {
    ...data.timePowerSingle || {},
    color: getStyleProp('--orange-500'),
  };
  return data;
};

export const applyEnergiesColors= (data: any): EnergySeries => {
  if (!data) {
    throw new Error('Missing telemetry');
  }
  data.fromGrid = {
    ...data.fromGrid || {},
    color: getStyleProp('--grey-400'),
  };
  data.consume = {
    ...data.consume || {},
    color: getStyleProp('--blue-500'),
  };
  data.selfConsume = {
    ...data.selfConsume || {},
    color: getStyleProp('--red-300'),
  };
  data.selfProduction = {
    ...data.selfProduction || {},
    color: getStyleProp('--purple-500'),
  };
  data.fromSolar = {
    ...data.fromSolar || {},
    color: getStyleProp('--orange-500'),
  };
  data.fromBattery = {
    ...data.fromBattery || {},
    color: getStyleProp('--green-500'),
  };
  data.toBattery = {
    ...data.toBattery || {},
    color: getStyleProp('--green-500'),
  };
  data.toGrid = {
    ...data.toGrid || {},
    color: getStyleProp('--grey-300'),
  };
  data.toHome = {
    ...data.toHome || {},
    color: getStyleProp('--blue-500'),
  };
  return data;
};

export const telemetryStyles = (data: string, isPositive: boolean = true) => {
  switch (data) {
    case 'EG1':
    case 'ET1':
      return {
        name: coreServicesLabels.energyGenerating,
        color: 'text-orange-500',
        bgColor: 'bg-yellow-300',
      };
    case 'P_DC_TOT':
      return {
        name: coreServicesLabels.powerGenerating,
        color: 'text-orange-500',
      };
    case 'EG2':
    case 'ET2':
      return {
        name: coreServicesLabels.energyExporting,
        color: 'text-grey-300',
        bgColor: 'bg-grey-100',
      };
    case 'P_EXP':
      return {
        name: isPositive ? coreServicesLabels.powerExporting : coreServicesLabels.powerImporting,
        color: 'text-grey-300',
      };
    case 'BP':
      return {
        name: `${coreServicesLabels.batteryPercentage}`,
        color: 'text-green-400',
      };
    case 'BP1':
      return {
        name: `${coreServicesLabels.batteryPercentage} 1`,
        color: 'text-green-400',
      };
    case 'BP2':
      return {
        name: `${coreServicesLabels.batteryPercentage} 2`,
        color: 'text-green-500',
      };
    case 'EG3':
    case 'ET3':
      return {
        name: coreServicesLabels.energyImporting,
        color: 'text-grey-400',
        bgColor: 'bg-grey-300',
      };
    case 'EG4':
    case 'ET4':
    case 'P_LOAD':
      return {
        name: data === 'P_LOAD' ? coreServicesLabels.powerConsuming : coreServicesLabels.energyConsuming,
        color: 'text-blue-500',
        bgColor: 'bg-blue-300',
      };
    case 'EG5':
    case 'ET5':
      return {
        name: coreServicesLabels.energyCharging,
        color: 'text-green-400',
        bgColor: 'bg-green-300',
      };
    case 'EG6':
    case 'ET6':
      return {
        name: coreServicesLabels.energyDischarging,
        color: 'text-green-500',
        bgColor: 'bg-green-100',
      };
    case 'SOL_EG':
      return {
        name: coreServicesLabels.energyIrradiation,
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-200',
      };
    case 'P_B_AGGR':
      return {
        name: isPositive ? coreServicesLabels.powerCharging : coreServicesLabels.powerDischarging,
        color: 'text-green-500',
      };
    case 'consume':
      return {
        name: coreServicesLabels.energyConsuming,
        color: 'text-blue-500',
        scssColor: '--blue-500',
      };
    case 'selfConsume':
      return {
        name: coreServicesLabels.selfConsume,
        color: 'text-red-400',
        scssColor: '--red-400',
        offsetCenter: ['0%', '-30%'],
        detail: {
          height: 10,
          fontSize: 14,
          borderWidth: 0,
          valueAnimation: true,
          offsetCenter: ['0%', '-15%'],
        },
      };
      case 'selfProduction':
      return {
        name: coreServicesLabels.selfProduction,
        color: 'text-purple-400',
        scssColor: '--purple-400',
        offsetCenter: ['0%', '-30%'],
        detail: {
          height: 10,
          fontSize: 14,
          borderWidth: 0,
          valueAnimation: true,
          offsetCenter: ['0%', '-15%'],
        },
      };
    case 'fromGrid':
      return {
        name: coreServicesLabels.fromGrid,
        color: 'text-grey-400',
        scssColor: '--grey-400',
        offsetCenter: ['0%', '0%'],
        detail: {
          height: 10,
          fontSize: 14,
          borderWidth: 0,
          valueAnimation: true,
          offsetCenter: ['0%', '15%'],
        },
      };
    case 'toGrid':
      return {
        name: coreServicesLabels.toGrid,
        color: 'text-grey-400',
        scssColor: '--grey-400',
        offsetCenter: ['0%', '0%'],
        detail: {
          height: 10,
          fontSize: 14,
          borderWidth: 0,
          valueAnimation: true,
          offsetCenter: ['0%', '15%'],
        },
      };
    case 'fromSolar':
      return {
        name: coreServicesLabels.fromSolar,
        color: 'text-orange-500',
        scssColor: '--orange-500',
        offsetCenter: ['0%', '0%'],
        detail: {
          height: 10,
          fontSize: 14,
          borderWidth: 0,
          valueAnimation: true,
          offsetCenter: ['0%', '15%'],
        },
      };
    case 'fromBattery':
      return {
        name: coreServicesLabels.fromBattery,
        color: 'text-green-500',
        scssColor: '--green-500',
        offsetCenter: ['0%', '30%'],
        detail: {
          height: 10,
          fontSize: 14,
          borderWidth: 0,
          valueAnimation: true,
          offsetCenter: ['0%', '45%'],
        },
      };
    case 'toBattery':
      return {
        name: coreServicesLabels.toBattery,
        color: 'text-green-500',
        scssColor: '--green-500',
        offsetCenter: ['0%', '30%'],
        detail: {
          height: 10,
          fontSize: 14,
          borderWidth: 0,
          valueAnimation: true,
          offsetCenter: ['0%', '45%'],
        },
      };
    case 'toHome':
      return {
        name: coreServicesLabels.toHome,
        color: 'text-blue-500',
        scssColor: '--blue-500',
        offsetCenter: ['0%', '30%'],
        detail: {
          height: 10,
          fontSize: 14,
          borderWidth: 0,
          valueAnimation: true,
          offsetCenter: ['0%', '45%'],
        },
      };
    case 'fast':
      return {
        name: coreServicesLabels.fast,
        color: 'text-red-500',
      };
    case 'timePowerSingle':
      return {
        name: coreServicesLabels.singleTimePower,
        color: 'text-orange-300',
      };
    case 'green':
      return {
        name: coreServicesLabels.green,
        color: 'text-green-500',
      };
    case 'greenMixed':
      return {
        name: coreServicesLabels.greenMixed,
        color: 'text-green-300',
      };
    case 'timePower':
      return {
        name: coreServicesLabels.timePower,
        color: 'text-orange-500',
      };
    case 'kmRepeat':
      return {
        name: coreServicesLabels.kilometer,
        color: 'text-yellow-500',
      };
    case 'kmSingle':
      return {
        name: coreServicesLabels.singleKilometer,
        color: 'text-yellow-300',
      };
    case 'minTh':
      return {
        name: coreServicesLabels.minTh,
        color: 'text-red-500',
        scss: '--red-500',
      };
    case 'maxTh':
      return {
        name: coreServicesLabels.maxTh,
        color: 'text-red-500',
        scss: '--red-500',
      };
    case 'emptyValue':
      return {
        name: 'emptyValue',
      };
  }
  throw Error('Power series not found');
};

export const customerPortalTelemetryStyles = (data: string) => {
  switch (data) {
    case 'EG1':
    case 'ET1':
      return {
        name: coreServicesLabels.energyGenerating,
        bgColor: 'tw-bg-yellow-100',
      };
    case 'EG2':
    case 'ET2':
      return {
        name: coreServicesLabels.energyExporting,
        bgColor: 'tw-bg-grey-100',
      };
    case 'EG3':
    case 'ET3':
      return {
        name: coreServicesLabels.energyImporting,
        bgColor: 'tw-bg-grey-200',
      };
    case 'EG4':
    case 'ET4':
      return {
        name: coreServicesLabels.energyConsuming,
        bgColor: 'tw-bg-blue-100',
      };
    case 'EG5':
    case 'ET5':
      return {
        name: coreServicesLabels.energyCharging,
        bgColor: 'tw-bg-green-200',
      };
    case 'EG6':
    case 'ET6':
      return {
        name: coreServicesLabels.energyDischarging,
        bgColor: 'tw-bg-green-100',
      };
    case 'SOL_EG':
      return {
        name: coreServicesLabels.energyDailyIrradiation,
        bgColor: 'tw-bg-yellow-200',
      };
    default:
      return {
        name: 'emptyValue',
        bgColor: 'tw-bg-white',
      };
  }
};


export const getBatteriesSocColors= (): string[] => {
  return [
    getStyleProp('--green-500'),
    getStyleProp('--yellow-500'),
    getStyleProp('--blue-500'),
    getStyleProp('--purple-500'),
    getStyleProp('--ottanio-500'),
    getStyleProp('--lime-500'),
    getStyleProp('--orange-500'),
    getStyleProp('--red-500'),
    getStyleProp('--green-300'),
    getStyleProp('--yellow-300'),
    getStyleProp('--blue-300'),
    getStyleProp('--purple-300'),
    getStyleProp('--ottanio-300'),
    getStyleProp('--lime-300'),
    getStyleProp('--orange-300'),
    getStyleProp('--red-300'),
  ]
}

export const getBatteriesSocBackgroundColors= (): string[] => {
  return [
    getStyleProp('--green-100'),
    getStyleProp('--yellow-100'),
    getStyleProp('--blue-100'),
    getStyleProp('--purple-100'),
    getStyleProp('--ottanio-100'),
    getStyleProp('--lime-100'),
    getStyleProp('--orange-100'),
    getStyleProp('--red-100'),
    getStyleProp('--green-100'),
    getStyleProp('--yellow-100'),
    getStyleProp('--blue-100'),
    getStyleProp('--purple-100'),
    getStyleProp('--ottanio-100'),
    getStyleProp('--lime-100'),
    getStyleProp('--orange-100'),
    getStyleProp('--red-100'),
  ]
}
